import { all, select } from "redux-saga/effects";

import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";

import AssociationsState from "@app/store/associations/AssociationsState";
import { getCurrentUser, getOrganizationId } from "@app/store/iam";
import { getUsers } from "@app/store/members";

import ChangeAssociationsPayload from "./ChangeAssociationsPayload";
import { findCompanies, findDeals, findPeople } from "./fetchHelpers";

export type NextAssociationsState = Pick<
  AssociationsState,
  "availableCompanies" | "availableDeals" | "availablePeople" | "deniedUsers" | "inaccessibleEntity"
>;

const getNextAssociationsState = function* getNextAssociationsState({
  assignee,
  company,
  deal,
  entityType,
  people,
  person,
}: ChangeAssociationsPayload): Generator<any, NextAssociationsState, any> {
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const currentUser: User = yield select(getCurrentUser);

  const peopleList = people ?? (person ? [person] : []);
  const primaryPerson = peopleList[0];
  const peopleAccountIds = Array.from(
    new Set(peopleList.flatMap((person) => person.accounts?.map(({ id }) => id) ?? []))
  );

  const [availableCompanies, availablePeople, availableDeals]: [
    Company[] | undefined,
    Person[] | undefined,
    Deal[] | undefined,
  ] = yield all([
    deal?.account?.id || peopleAccountIds.length
      ? findCompanies(orgId, [...(deal?.account?.id ? [deal.account.id] : []), ...peopleAccountIds])
      : undefined,
    company?.id ? findPeople(orgId, company?.id) : undefined,
    // With 2+ people, suggesting deals tied only to the primary person would be
    // misleading — either show deals for all or none. Skip in that case.
    entityType !== EntityType.ACTIVITY ||
    (!company?.id && !primaryPerson?.id) ||
    peopleList.length > 1
      ? undefined
      : findDeals(orgId, company?.id, primaryPerson?.id),
  ]);

  let deniedUsers: undefined | User[];
  let inaccessibleEntity = false;

  if (entityType === EntityType.ACTIVITY) {
    const allUsers: User[] = yield select(getUsers);

    // when assignee is not set explicitly, we assume it's current user
    const assigneeId = assignee?.id ?? currentUser.id;
    deniedUsers = allUsers.filter(
      ({ id }) =>
        company?.userIds?.includes(id) === false ||
        primaryPerson?.userIds?.includes(id) === false ||
        deal?.userIds?.includes(id) === false
    );

    inaccessibleEntity = new Set(deniedUsers.map((user) => user.id)).has(assigneeId);
  }
  return {
    availableCompanies: availableCompanies ?? [],
    availableDeals: availableDeals ?? [],
    availablePeople: availablePeople ?? [],
    deniedUsers,
    inaccessibleEntity,
  };
};

export default getNextAssociationsState;
